import { api } from "@/lib/http";
import type {
  FurnitureCategory,
  FurnitureItem,
  FurnitureReservationHistoryEntry,
  StorageCategory,
  StorageLocation,
} from "@/types/api";
import type { Furniture } from "@/types/api";

export interface FurnitureListParams {
  skip?: number;
  limit?: number;
  search?: string;
  categoryId?: number;
  /** "size" orders by width, then height (both descending), then name. */
  sort?: "size";
}

export interface FurnitureListResponse {
  items: Furniture[];
  total: number;
}

export interface FurnitureCategoryRollupParams {
  search?: string;
  categoryId?: number;
}

export interface FurnitureCategoryRollup {
  category: string;
  in: number;
  out: number;
  selected: number;
  total: number;
  available: number;
}

export type FurnitureCategoryItemsFilter = "in" | "out" | "selected" | "all";

export interface FurnitureCategoryItemsParams {
  search?: string;
  category: string;
  filter?: FurnitureCategoryItemsFilter;
  skip?: number;
  limit?: number;
}

export interface FurnitureCategoryItemsResponse {
  items: FurnitureItem[];
  total: number;
}

export interface FurnitureItemInput {
  id?: number | null;
  status: number;
  purchaseDate: string | null;
  purchaseCost: number | null;
  storageLocation: number | null;
  notes: string;
}

export interface SaveFurnitureBody {
  name: string;
  category: number | null;
  width: number | null;
  height: number | null;
  length: number | null;
  notes: string;
  image: number | null;
  tags: string[];
  items: FurnitureItemInput[];
}

export interface EnsureStorageLocationBody {
  categoryId: number;
  bin: string;
  slot: string;
}

export const inventoryApi = {
  listFurniture: (params: FurnitureListParams = {}) =>
    api.get<FurnitureListResponse>(
      "/api/furniture",
      params as Record<string, string | number | undefined>,
    ),
  listCategoryRollup: (params: FurnitureCategoryRollupParams = {}) =>
    api.get<FurnitureCategoryRollup[]>(
      "/api/furniture/categoryRollup",
      params as Record<string, string | number | undefined>,
    ),
  listCategoryItems: (params: FurnitureCategoryItemsParams) =>
    api.get<FurnitureCategoryItemsResponse>(
      "/api/furniture/categoryItems",
      params as unknown as Record<string, string | number | undefined>,
    ),
  getFurniture: (id: number) => api.get<Furniture>(`/api/furniture/${id}`),
  listReservationHistory: (id: number) =>
    api.get<FurnitureReservationHistoryEntry[]>(`/api/furniture/${id}/reservationHistory`),
  createFurniture: (body: SaveFurnitureBody) => api.post<Furniture>("/api/furniture", body),
  updateFurniture: (id: number, body: SaveFurnitureBody) =>
    api.put<Furniture>(`/api/furniture/${id}`, body),
  removeFurniture: (id: number) => api.del<void>(`/api/furniture/${id}`),
  availableItems: () => api.get<FurnitureItem[]>("/api/furnitureItems/available"),

  listCategories: () => api.get<FurnitureCategory[]>("/api/categories"),

  listStorageCategories: () => api.get<StorageCategory[]>("/api/storageCategories"),
  listStorageLocations: (categoryId?: number) =>
    api.get<StorageLocation[]>("/api/storageLocations", categoryId ? { categoryId } : {}),
  ensureStorageLocation: (body: EnsureStorageLocationBody) =>
    api.post<StorageLocation>("/api/storageLocations/ensure", body),
};
